import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Plane, Waves } from "lucide-react";

const PROGRAMS = [
  {
    slug: "agrilearn",
    num: "01",
    title: "AgriLearn Campus",
    tagline: "Earth-First Education",
    focus: "Agriculture · Ecology · Sustainability",
    grades: "Nursery-Grade 12",
    brief: "Immersive farm experience across 7.5 acres of living science. From soil to seed, students discover where food comes from.",
    image: process.env.REACT_APP_AGRILEARN_IMG,
    icon: Leaf,
  },
  {
    slug: "aerolab",
    num: "02",
    title: "AeroLab Experience",
    tagline: "The Physics of Flight",
    focus: "Aviation · Physics · Aerospace",
    grades: "Grade 5-Grade 12",
    brief: "Step inside real aircraft at a private aerodrome. Hands-on exposure to aerodynamics and aerospace engineering.",
    image: process.env.REACT_APP_AEROLAB_IMG,
    icon: Plane,
  },
  {
    slug: "sailscience",
    num: "03",
    title: "SailScience Program",
    tagline: "Mastering the Elements",
    focus: "Maritime Science · Leadership",
    grades: "Grade 5-Grade 12",
    brief: "Take the helm on open water. Physics becomes personal when students command the wind and waves.",
    image: process.env.REACT_APP_SAILSCIENCE_IMG,
    icon: Waves,
  },
];

export default function ProgramsSection() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add("visible"); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="programs" data-testid="programs-section" ref={ref} className="reveal-section py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">
            Our Signature Programs
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground mb-4">
            Three Worlds. Infinite Discovery.
          </h2>
          <p className="text-base text-muted-foreground font-light max-w-2xl mx-auto">
            Fully immersive, curriculum-aligned programs-each set in a distinct environment,
            each designed to transform how your students understand the world.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {PROGRAMS.map((program) => {
            const Icon = program.icon;
            return (
              <Link
                key={program.slug}
                to={`/programs/${program.slug}`}
                data-testid={`program-card-${program.slug}`}
                className="group relative overflow-hidden bg-card border border-border/60 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={program.image}
                    alt={`${program.title} - Bangalore school trip`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/80 via-[#0D1B2A]/20 to-transparent" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-sm">
                    <span className="text-xs font-semibold tracking-wider text-primary">PROGRAM {program.num}</span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-accent" />
                      <span className="text-xs text-white/70 font-light">{program.focus}</span>
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-white tracking-tight">
                      {program.title}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-sm font-medium text-accent mb-2">{program.tagline}</p>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed mb-4">
                    {program.brief}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">
                      {program.grades}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:text-accent transition-colors">
                      Explore More
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
